import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Copy, Check, Sparkles, Heart, Shield, Crown, Zap, Info, ArrowRight } from 'lucide-react';
import { useData } from '../context/DataContext';

interface SupporterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SupporterModal: React.FC<SupporterModalProps> = ({ isOpen, onClose }) => {
  const { data, showToast } = useData();
  const [copied, setCopied] = useState(false);
  const pixKey = "c5e115ec-0ef4-4e46-8853-c261bc43d81d";

  const handleCopyPix = () => {
    navigator.clipboard.writeText(pixKey);
    setCopied(true);
    showToast("Chave PIX copiada!", "success");
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] bg-slate-950/90 flex items-center justify-center p-4 backdrop-blur-md animate-fade-in" onClick={onClose}>
      <div 
        className="bg-slate-900 w-full max-w-4xl rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden relative animate-scale-in"
        onClick={e => e.stopPropagation()}
      >
        {/* Header Background Gradient */}
        <div className="absolute top-0 inset-x-0 h-48 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent pointer-events-none"></div>
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 z-50 p-3 bg-black/20 hover:bg-black/40 text-white/50 hover:text-white rounded-full transition-all backdrop-blur-md border border-white/5"
        >
          <X size={20} />
        </button>

        <div className="relative z-10 flex flex-col md:flex-row h-full max-h-[90vh] overflow-y-auto md:overflow-hidden">
          {/* Left Column: QR Code & Key */}
          <div className="w-full md:w-[45%] p-8 md:p-12 flex flex-col items-center justify-center bg-black/20 border-r border-white/5">
            <div className="mb-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-amber-500 shadow-lg shadow-amber-500/20 flex items-center justify-center text-black mx-auto mb-4">
                <Crown size={32} />
              </div>
              <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Área do Apoiador</h2>
              <p className="text-amber-500/80 font-bold text-xs uppercase tracking-widest mt-1">Sua contribuição faz a diferença</p>
            </div>

            <div className="bg-white p-4 rounded-[2rem] shadow-2xl shadow-black/50 mb-8 group relative">
              <div className="w-52 h-52 bg-white flex items-center justify-center rounded-2xl overflow-hidden p-2">
                <img src="/images/qrcode-inter.jpeg" alt="PIX QR Code" className="w-full h-full object-contain" />
              </div>
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-full border border-white/10 shadow-lg whitespace-nowrap">
                Escaneie para Pagar
              </div>
            </div>

            <div className="w-full space-y-3">
              <div className="text-center">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Chave PIX (E-mail)</span>
              </div>
              <div className="flex items-center gap-2 p-1.5 bg-white/5 border border-white/10 rounded-2xl group/key">
                <div className="flex-1 px-4 py-2 text-sm font-mono text-slate-300 truncate font-bold">
                  {pixKey}
                </div>
                <button 
                  onClick={handleCopyPix}
                  className={`p-3 rounded-xl transition-all flex items-center gap-2 ${copied ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-black hover:bg-amber-400 px-5'}`}
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                  <span className="text-xs font-black uppercase tracking-wider">{copied ? 'Copiado' : 'Copiar'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Benefits & Info */}
          <div className="w-full md:w-[55%] p-8 md:p-12 flex flex-col justify-between space-y-8 overflow-y-auto">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-3">Por que apoiar?</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  O **Games no Linux** é um projeto independente dedicado à comunidade Hytale. 
                  Seu apoio direto financia nossa infraestrutura de servidores, desenvolvimento de mods exclusivos e a manutenção de um ambiente livre e aberto para todos.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                 <BenefitItem icon={Shield} label="Nomes Coloridos & Tags" />
                 <BenefitItem icon={Crown} label="Ranking de Honra Global" />
                 <BenefitItem icon={Zap} label="Acesso Antecipado a Mods" />
                 <BenefitItem icon={Heart} label="Badge de Apoiador" />
              </div>

              <div className="bg-blue-500/5 border border-blue-500/10 rounded-2xl p-5 flex gap-4">
                <div className="p-3 rounded-xl bg-blue-500/10 text-blue-500 shrink-0 self-start">
                  <Info size={20} />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-blue-300 uppercase tracking-widest mb-1">Como resgatar?</h4>
                  <p className="text-[11px] text-blue-400/80 leading-relaxed font-medium">
                    Após realizar o PIX, envie o comprovante no nosso **Discord** para nossa equipe vincular sua conta e ativar seus benefícios instantaneamente.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-white/5 flex items-center justify-between">
              <div className="flex -space-x-3">
                {[1,2,3,4].map(i => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-slate-900 bg-slate-800 flex items-center justify-center text-[10px] font-bold text-slate-400">
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
                <div className="w-8 h-8 rounded-full border-2 border-slate-900 bg-amber-500 flex items-center justify-center text-[10px] font-bold text-black ring-2 ring-amber-500/20">
                  +
                </div>
              </div>
              <button 
                onClick={onClose}
                className="text-xs font-bold text-slate-500 hover:text-white transition-colors uppercase tracking-widest flex items-center gap-2 group"
              >
                Voltar para o Reino
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const BenefitItem: React.FC<{ icon: any, label: string }> = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/5 rounded-xl">
    <Icon size={16} className="text-amber-500" />
    <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">{label}</span>
  </div>
);
